/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { alias } from '@ember/object/computed';
import Component from '@glimmer/component';
import AuthService from 'cio/services/auth';
import LogModel from 'cio/models/log';
import GroupCondition from 'cio/app-models/conditions/group';
import { task } from 'ember-concurrency';
import { waitForQueue } from 'ember-concurrency';
import { timeout } from 'ember-concurrency';
import { queryEncode } from 'cio/helpers/query';
import { serializeFilters } from 'cio/utils/conditions/serialize/form';
import config from 'cio/config/environment';
import { tracked } from '@glimmer/tracking';
import FormEventAttributeCondition from 'cio/app-models/conditions/form-event-attribute';
import ArrayProxy from '@ember/array/proxy';
import ApiService from 'cio/services/api';
import DS from 'ember-data';
import FacebookLeadAdsForm from 'cio/models/facebook-lead-ads-form';
import CustomForm from 'cio/models/custom-form';

const DEBOUNCE_DELAY = config.APP.shortDelay;

function filterEvents(logs: ArrayProxy<LogModel>, matches: string[]) {
  return logs.filter((l: LogModel) => matches.includes(l.id));
}

interface RowArgs {
  index: number;
  alreadySelectedForms: (FacebookLeadAdsForm | CustomForm)[];
  selectedForm: FacebookLeadAdsForm | CustomForm | undefined;
  selectFormAction: (form: FacebookLeadAdsForm | CustomForm | null) => unknown;
  removeFormAction: () => unknown;
  addNewFormAction: () => unknown;
  isDisabled: boolean;
  addFilterAction: () => unknown;
  removeFilterAction: () => unknown;
  eventDataFilters?: GroupCondition;
}

export default class Row extends Component<RowArgs> {
  @service auth!: AuthService;
  @service api!: ApiService;
  @service store!: DS.Store;

  @tracked eventLogsBase: LogModel[] = [];
  @tracked eventLogsWithDataFilters: LogModel[] = [];

  @alias('updateEventLogsTask.isRunning') refreshingEventLogsDataFilters!: boolean;

  // tracks whether update logs task was called with dataFilters=false:
  get refreshingEventLogsBase() {
    let taskInstance = this.updateEventLogsTask.lastRunning;
    if (!taskInstance) {
      return false;
    }

    // @ts-ignore: "taskInstance.args" not typed, but present
    let [updatingOnlyForDataFilters] = taskInstance.args || [];

    return !updatingOnlyForDataFilters;
  }

  constructor(owner: unknown, args: RowArgs) {
    super(owner, args);

    this.updateEventLogsTask.perform(false);
  }

  async getEventLogs(dataFilters?: boolean) {
    if (!this.args.selectedForm) {
      return [];
    }

    let formCondition = FormEventAttributeCondition.create({
      name: 'form_id',
      formType: this.args.selectedForm.formType,
      value: this.args.selectedForm.formId,
    });

    let condition = dataFilters
      ? GroupCondition.create({
          operator: 'and',
          children: [formCondition, this.args.eventDataFilters as GroupCondition],
        })
      : formCondition;

    let query = {
      type: 'event',
      name: 'form_submit',
      since: true,
      filters: queryEncode(serializeFilters([condition], 'and', true)),
    };

    try {
      let result = await this.store.query('log', query);

      return filterEvents(result, (result as any).meta.filter_matches);
    } catch (e) {
      // do nothing: fallback to empty logs array
      return [];
    }
  }

  updateEventLogsTask = task(async (onlyDataFilters?: boolean) => {
    await waitForQueue('actions');
    await timeout(DEBOUNCE_DELAY);

    if (!onlyDataFilters) {
      this.eventLogsBase = await this.getEventLogs(false);
    }

    // if no extra data filters are defined, we can use same logs as for the base form filter:
    this.eventLogsWithDataFilters = this.args.eventDataFilters ? await this.getEventLogs(true) : this.eventLogsBase;
  });

  @action async onRemoveFilter() {
    await this.args.removeFilterAction();
    this.updateEventLogsTask.perform(true);
  }

  @action
  async onConditionChange() {
    this.updateEventLogsTask.perform(true);
  }
}
